import pick from 'lodash/pick';
import {
  createStripeAccount,
  updateStripeAccount,
  fetchStripeAccount,
} from '../../ducks/stripeConnectAccount.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { createRecipientApi, destroyRecipientApi, retrieveRecipientApi } from '../../util/api';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { getSubMerchantIdFromUser } from '../../util/genericHelpers';
import {
  buildInvalidPayoutDetailsSnapshot,
  buildPayoutDetailsSnapshot,
} from '../../util/payoutDetails';

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/StripePayoutPage/SET_INITIAL_VALUES';
export const SAVE_PAYOUT_DETAILS_REQUEST = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_REQUEST';
export const SAVE_PAYOUT_DETAILS_SUCCESS = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_SUCCESS';
export const SAVE_PAYOUT_DETAILS_ERROR = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_ERROR';

export const CREATE_OMISE_RECIPIENT_REQUEST = 'app/StripePayoutPage/CREATE_OMISE_RECIPIENT_REQUEST';
export const CREATE_OMISE_RECIPIENT_SUCCESS = 'app/StripePayoutPage/CREATE_OMISE_RECIPIENT_SUCCESS';
export const CREATE_OMISE_RECIPIENT_ERROR = 'app/StripePayoutPage/CREATE_OMISE_RECIPIENT_ERROR';

export const SET_OMISE_RECIPIENT_DATA_REQUEST =
  'app/StripePayoutPage/SET_OMISE_RECIPIENT_DATA_REQUEST';
export const SET_OMISE_RECIPIENT_DATA_SUCCESS =
  'app/StripePayoutPage/SET_OMISE_RECIPIENT_DATA_SUCCESS';
export const SET_OMISE_RECIPIENT_DATA_ERROR = 'app/StripePayoutPage/SET_OMISE_RECIPIENT_DATA_ERROR';

export const DESTROY_OMISE_RECIPIENT_REQUEST =
  'app/StripePayoutPage/DESTROY_OMISE_RECIPIENT_REQUEST';
export const DESTROY_OMISE_RECIPIENT_SUCCESS =
  'app/StripePayoutPage/DESTROY_OMISE_RECIPIENT_SUCCESS';
export const DESTROY_OMISE_RECIPIENT_ERROR = 'app/StripePayoutPage/DESTROY_OMISE_RECIPIENT_ERROR';

// ================ Reducer ================ //

const initialState = {
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  fromReturnURL: false,
  omiseRecipientInProgress: false,
  omiseRecipientCreated: false,
  omiseRecipientError: null,
  omiseRecipientData: null,
  omiseRecipientDataInProgress: false, // keeping original key as in your code
  omiseRecipientDataError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SET_OMISE_RECIPIENT_DATA_REQUEST:
      return { ...state, omiseRecipientDataInProgress: true };
    case SET_OMISE_RECIPIENT_DATA_SUCCESS:
      return { ...state, omiseRecipientData: payload, omiseRecipientDataInProgress: false };
    case SET_OMISE_RECIPIENT_DATA_ERROR:
      return { ...state, omiseRecipientDataError: payload, omiseRecipientDataInProgress: false };

    case SAVE_PAYOUT_DETAILS_REQUEST:
      return { ...state, payoutDetailsSaveInProgress: true };
    case SAVE_PAYOUT_DETAILS_ERROR:
      return { ...state, payoutDetailsSaveInProgress: false };
    case SAVE_PAYOUT_DETAILS_SUCCESS:
      return { ...state, payoutDetailsSaveInProgress: false, payoutDetailsSaved: true };

    case CREATE_OMISE_RECIPIENT_REQUEST:
      return {
        ...state,
        omiseRecipientInProgress: true,
        omiseRecipientCreated: false,
        omiseRecipientError: null,
      };
    case CREATE_OMISE_RECIPIENT_SUCCESS:
      return { ...state, omiseRecipientInProgress: false, omiseRecipientCreated: true };
    case CREATE_OMISE_RECIPIENT_ERROR:
      return { ...state, omiseRecipientInProgress: false, omiseRecipientError: payload };

    case DESTROY_OMISE_RECIPIENT_REQUEST:
      return { ...state, omiseRecipientInProgress: true, omiseRecipientError: null };
    case DESTROY_OMISE_RECIPIENT_SUCCESS:
      return {
        ...state,
        omiseRecipientInProgress: false,
        omiseRecipientCreated: false,
        omiseRecipientData: null,
      };
    case DESTROY_OMISE_RECIPIENT_ERROR:
      return { ...state, omiseRecipientInProgress: false, omiseRecipientError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const savePayoutDetailsRequest = () => ({ type: SAVE_PAYOUT_DETAILS_REQUEST });
export const savePayoutDetailsError = () => ({ type: SAVE_PAYOUT_DETAILS_ERROR });
export const savePayoutDetailsSuccess = () => ({ type: SAVE_PAYOUT_DETAILS_SUCCESS });

export const createOmiseRecipientRequest = () => ({ type: CREATE_OMISE_RECIPIENT_REQUEST });
export const createOmiseRecipientSuccess = () => ({ type: CREATE_OMISE_RECIPIENT_SUCCESS });
export const createOmiseRecipientError = e => ({ type: CREATE_OMISE_RECIPIENT_ERROR, payload: e });

export const setOmiseRecipientDataRequest = () => ({ type: SET_OMISE_RECIPIENT_DATA_REQUEST });
export const setOmiseRecipientDataSuccess = recipient => ({
  type: SET_OMISE_RECIPIENT_DATA_SUCCESS,
  payload: recipient,
});
export const setOmiseRecipientDataError = e => ({
  type: SET_OMISE_RECIPIENT_DATA_ERROR,
  payload: e,
});

export const destroyOmiseRecipientRequest = () => ({ type: DESTROY_OMISE_RECIPIENT_REQUEST });
export const destroyOmiseRecipientSuccess = () => ({ type: DESTROY_OMISE_RECIPIENT_SUCCESS });
export const destroyOmiseRecipientError = e => ({
  type: DESTROY_OMISE_RECIPIENT_ERROR,
  payload: e,
});

// ================ Helpers ================ //

const selectRecipientId = state =>
  state?.user?.currentUser?.attributes?.profile?.publicData?.omiseRecipientId;

const markStoredRecipientInvalid = async ({ dispatch, currentUser, recipientId, reason }) => {
  const existingPrivateData = currentUser?.attributes?.profile?.privateData || {};
  const payoutDetails = buildInvalidPayoutDetailsSnapshot(currentUser, { recipientId, reason });

  await dispatch(
    updateProfile({
      publicData: { omiseRecipientId: null },
      privateData: { ...existingPrivateData, payoutDetails },
    })
  );
};

// ================ Thunks ================ //

export const savePayoutDetails = (values, isUpdateCall) => (dispatch, getState, sdk) => {
  const upsertThunk = isUpdateCall ? updateStripeAccount : createStripeAccount;
  dispatch(savePayoutDetailsRequest());

  return dispatch(upsertThunk(values, { expand: true }))
    .then(response => {
      dispatch(savePayoutDetailsSuccess());
      return response;
    })
    .catch(() => dispatch(savePayoutDetailsError()));
};

export const createOmiseRecipient = values => async (dispatch, getState) => {
  dispatch(createOmiseRecipientRequest());

  const state = getState();
  const existingRecipientId = selectRecipientId(state) || null;
  const subMerchantId = getSubMerchantIdFromUser(state.user.currentUser);
  const requestPayload = {
    ...values,
    subMerchantId,
    recipientId: existingRecipientId, // pass through if we already have one
  };

  try {
    const response = await createRecipientApi(requestPayload);
    const newId = response?.recipient?.id;
    const newRecipient = response?.recipient || null;
    if (newRecipient) {
      dispatch(setOmiseRecipientDataSuccess(newRecipient));
    }

    const currentUser = state.user?.currentUser;
    const existingPublicData = currentUser?.attributes?.profile?.publicData || {};
    const existingPrivateData = currentUser?.attributes?.profile?.privateData || {};
    const recipientIdToStore = newId || existingRecipientId;
    const payoutDetailsSnapshot = buildPayoutDetailsSnapshot(values);

    if (recipientIdToStore) {
      payoutDetailsSnapshot.recipientId = recipientIdToStore;
    }

    const nextPublicData = recipientIdToStore
      ? { ...existingPublicData, omiseRecipientId: recipientIdToStore }
      : existingPublicData;
    const nextPrivateData = {
      ...existingPrivateData,
      payoutDetails: payoutDetailsSnapshot,
    };

    await dispatch(updateProfile({ publicData: nextPublicData, privateData: nextPrivateData }));
    await dispatch(fetchCurrentUser());

    dispatch(createOmiseRecipientSuccess());
    return newId;
  } catch (error) {
    dispatch(createOmiseRecipientError(error));
    throw error;
  }
};

export const destroyOmiseRecipient = recipientId => async (dispatch, getState) => {
  dispatch(destroyOmiseRecipientRequest());
  try {
    await destroyRecipientApi({ recipientId });
    // Remove the linkage from the user's profile
    await dispatch(updateProfile({ publicData: { omiseRecipientId: null } }));
    await dispatch(fetchCurrentUser());

    // Clear local recipient data
    dispatch(setOmiseRecipientDataSuccess(null));
    dispatch(destroyOmiseRecipientSuccess());
    return true;
  } catch (error) {
    console.error('Destroy Omise recipient failed:', error);
    dispatch(destroyOmiseRecipientError(error));
    throw error;
  }
};

export const retrieveOmiseRecipient = () => async (dispatch, getState) => {
  const state = getState();
  const recipientId = selectRecipientId(state);

  // 🚫 No recipient? Do nothing (don’t flip loading state)
  if (!recipientId) {
    return null;
  }

  dispatch(setOmiseRecipientDataRequest());

  try {
    const subMerchantId = getSubMerchantIdFromUser(state.user.currentUser);
    const response = await retrieveRecipientApi({ recipientId, subMerchantId });
    if (response?.recipientNotFound) {
      const currentUser = getState().user.currentUser;
      const reason = response?.reason || 'OMISE_RECIPIENT_NOT_FOUND';
      dispatch(setOmiseRecipientDataSuccess(null));
      try {
        await markStoredRecipientInvalid({ dispatch, currentUser, recipientId, reason });
        await dispatch(fetchCurrentUser());
      } catch (profileError) {
        console.warn('Failed to mark stale Omise recipient invalid:', profileError);
      }
      return null;
    }

    const recipient = response?.recipient;
    dispatch(setOmiseRecipientDataSuccess(recipient || null));
    return recipient || null;
  } catch (error) {
    dispatch(setOmiseRecipientDataError(error));
    return null;
  }
};

export const loadData = () => async (dispatch, getState, sdk) => {
  dispatch(setInitialValues());

  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };

  const userResponse = await dispatch(fetchCurrentUser(fetchCurrentUserOptions));

  const currentUser = getState().user.currentUser;
  if (currentUser?.stripeAccount) {
    dispatch(fetchStripeAccount());
  }

  const recipientId = selectRecipientId(getState());

  // 🔁 Load Omise recipient ONLY if it exists
  if (recipientId) {
    await dispatch(retrieveOmiseRecipient());
  }

  return userResponse;
};
